import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { es } from "date-fns/locale";
import { format, isSameDay } from "date-fns";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { SALAS, HORAS } from "../config/constants";
import { videoService } from "../services/api";
import { formatBytes, formatTime } from "../utils/formatters";

const UploadForm = () => {
  // Estado para el límite de subida dinámico
  const [maxFileSizeGB, setMaxFileSizeGB] = useState(2.5);
  const [maxFileSizeBytes, setMaxFileSizeBytes] = useState(2.5 * 1024 * 1024 * 1024);

  // Estados para el formulario
  const [sala, setSala] = useState("");
  const [fecha, setFecha] = useState(null);
  const [hora, setHora] = useState("");
  const [marca, setMarca] = useState("");
  const [file, setFile] = useState(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Estados para la UI
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [eta, setEta] = useState(Infinity);
  const [uploadStage, setUploadStage] = useState("idle");
  
  // Estado para modal de confirmación
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  // Refs para cálculos de progreso
  const progressDataRef = useRef({
    startTime: null,
    lastLoaded: 0,
    lastTime: null,
  });

  // Cargar el límite de subida desde el servidor
  useEffect(() => {
    const loadUploadLimit = async () => {
      try {
        const response = await videoService.getUploadLimit();
        if (response.success) {
          setMaxFileSizeGB(response.data.maxFileSizeGB);
          setMaxFileSizeBytes(response.data.maxFileSizeBytes);
        }
      } catch (err) {
        console.error("Error cargando límite de subida:", err);
      }
    };
    loadUploadLimit();
  }, []);

  // Limpiar datos de progreso cuando no se esté cargando
  useEffect(() => {
    if (uploadStage === "idle") {
      progressDataRef.current = {
        startTime: null,
        lastLoaded: 0,
        lastTime: null,
      };
      setProgress(0);
      setSpeed(0);
      setEta(Infinity);
    }
  }, [uploadStage]);

  // Manejador de cambio de archivo
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    // Validar que sea un archivo
    if (!selectedFile) {
      setFile(null);
      return;
    }

    // Validar que sea un archivo MP4
    if (selectedFile.type !== "video/mp4") {
      setError("Solo se permiten archivos .mp4");
      setFile(null);
      return;
    }

    // Validar el tamaño contra el límite configurable
    if (selectedFile.size > maxFileSizeBytes) {
      setError(`El archivo no debe superar los ${maxFileSizeGB}GB`);
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setError("");
  };

  // Manejador de envío de formulario (validación y apertura de modal)
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validar todos los campos
    if (!sala) {
      setError("Por favor, selecciona una sala");
      return;
    }

    if (!fecha) {
      setError("Por favor, selecciona una fecha");
      return;
    }

    if (!hora) {
      setError("Por favor, selecciona una hora");
      return;
    }

    if (!marca) {
      setError("Por favor, ingresa el nombre de la marca");
      return;
    }

    if (!file) {
      setError("Por favor, selecciona un archivo de video");
      return;
    }

    // Si todo está validado, abrir modal de confirmación
    setError("");
    setConfirmModalOpen(true);
  };

  // Manejador de subida confirmada
  const handleConfirmedUpload = async () => {
    // Cerrar modal
    setConfirmModalOpen(false);

    // Crear FormData para enviar
    const formData = new FormData();
    formData.append("video", file);
    formData.append("sala", sala);
    formData.append("fecha", fecha.toISOString());
    formData.append("hora", hora);
    formData.append("marca", marca);

    setLoading(true);
    setError("");
    setSuccess("");
    setUploadStage("uploadingToServer");
    progressDataRef.current = {
      startTime: null,
      lastLoaded: 0,
      lastTime: null,
    };
    setProgress(0);
    setSpeed(0);
    setEta(Infinity);

    try {
      const response = await videoService.uploadVideo(
        formData,
        (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const currentTime = Date.now();
            const { startTime, lastLoaded } = progressDataRef.current;

            if (!startTime) {
              progressDataRef.current.startTime = currentTime;
              progressDataRef.current.lastTime = currentTime;
            }

            const currentLoaded = progressEvent.loaded;
            const totalSize = progressEvent.total;
            const currentLastTime =
              progressDataRef.current.lastTime ?? currentTime;

            const elapsedTimeSinceLast = (currentTime - currentLastTime) / 1000;
            const loadedSinceLast = currentLoaded - lastLoaded;

            let currentSpeed = 0;
            if (elapsedTimeSinceLast > 0.1) {
              currentSpeed = loadedSinceLast / elapsedTimeSinceLast;
              setSpeed(currentSpeed);
              progressDataRef.current.lastLoaded = currentLoaded;
              progressDataRef.current.lastTime = currentTime;
            } else if (elapsedTimeSinceLast <= 0.1 && speed > 0) {
              currentSpeed = speed;
            } else {
              currentSpeed = speed;
            }

            const percentComplete = Math.round(
              (currentLoaded / totalSize) * 100
            );
            setProgress(percentComplete);

            const remainingBytes = totalSize - currentLoaded;
            const currentEta =
              currentSpeed > 0 ? remainingBytes / currentSpeed : Infinity;
            setEta(currentEta);

            if (percentComplete === 100) {
              setTimeout(() => setUploadStage("processingOnServer"), 200);
            }
          }
        }
      );

      setUploadStage("idle");
      if (response.success) {
        setSuccess("¡Video subido correctamente!");
        // Limpiar el formulario
        setSala("");
        setFecha(null);
        setHora("");
        setMarca("");
        setFile(null);
        // Limpiar el input de archivo
        document.getElementById("video-upload").value = "";
      } else {
        setError(response.message || "Error al subir el video");
      }
    } catch (error) {
      setUploadStage("idle");
      setError(error.message || "Error al subir el video");
    } finally {
      setLoading(false);
    }
  };

  // Función helper para obtener el nombre de la sala
  const getSalaName = () => {
    const salaObj = SALAS.find(s => s.id === sala);
    return salaObj ? salaObj.nombre : sala;
  };

  // Función helper para verificar si la fecha es hoy
  const isToday = () => {
    if (!fecha) return true;
    return isSameDay(fecha, new Date());
  };

  // Función helper para formatear la fecha con mayúsculas
  const formatFechaCompleta = (date) => {
    if (!date) return "";
    const fechaFormateada = format(date, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: es });
    // Capitalizar la primera letra del día de la semana y del mes
    // Ejemplo: "martes, 21 de octubre de 2025" -> "Martes, 21 de Octubre de 2025"
    const palabras = fechaFormateada.split(' ');
    return palabras
      .map((word, index) => {
        // Capitalizar la primera palabra (día) y la cuarta palabra (mes)
        // Posición 0: día, 1: número, 2: "de", 3: mes, 4: "de", 5: año
        if (index === 0 || index === 3) {
          return word.charAt(0).toUpperCase() + word.slice(1);
        }
        return word;
      })
      .join(' ');
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom align="center">
            Subir Video
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="sala-label">Sala</InputLabel>
              <Select
                labelId="sala-label"
                value={sala}
                onChange={(e) => setSala(e.target.value)}
                label="Sala"
                disabled={loading}
              >
                {SALAS.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={es}
            >
              <DatePicker
                label="Fecha"
                value={fecha}
                onChange={(newValue) => setFecha(newValue)}
                open={isCalendarOpen}
                onOpen={() => setIsCalendarOpen(true)}
                onClose={() => setIsCalendarOpen(false)}
                disabled={loading}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: "normal",
                    onClick: () => setIsCalendarOpen(true),
                  },
                }}
                sx={{ width: "100%", mb: 2 }}
              />
            </LocalizationProvider>

            <FormControl fullWidth margin="normal">
              <InputLabel id="hora-label">Hora Comienzo Charla</InputLabel>
              <Select
                labelId="hora-label"
                value={hora}
                onChange={(e) => setHora(e.target.value)}
                label="Hora Comienzo Charla"
                disabled={loading}
              >
                {HORAS.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Nombre de la Marca"
              value={marca}
              onChange={(e) => setMarca(e.target.value)}
              fullWidth
              margin="normal"
              disabled={loading}
              helperText="Este texto se convertirá a mayúsculas, sin tildes y con guiones en lugar de espacios."
            />

            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<CloudUploadIcon />}
              sx={{ mt: 2, mb: 1 }}
              disabled={loading}
            >
              {file ? file.name : `Seleccionar Video (.mp4, máx ${maxFileSizeGB}GB)`}
              <input
                id="video-upload"
                type="file"
                accept="video/mp4"
                hidden
                onChange={handleFileChange}
                disabled={loading}
              />
            </Button>

            {loading && (
              <Box sx={{ width: "100%", mt: 1, mb: 2 }}>
                {uploadStage === "uploadingToServer" && (
                  <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                    <Box sx={{ width: "100%", mr: 1 }}>
                      <LinearProgress variant="determinate" value={progress} />
                    </Box>
                    <Box sx={{ minWidth: 35 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >{`${progress}%`}</Typography>
                    </Box>
                  </Box>
                )}
                {uploadStage === "processingOnServer" && <LinearProgress />}

                <Typography
                  variant="caption"
                  display="block"
                  gutterBottom
                  sx={{ textAlign: "center" }}
                >
                  {uploadStage === "uploadingToServer" &&
                    `Subiendo al servidor... ${formatBytes(
                      speed
                    )}/s - ETA: ${formatTime(eta)}`}
                  {uploadStage === "processingOnServer" &&
                    `Procesando y subiendo a Google Drive...`}
                </Typography>
              </Box>
            )}

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              sx={{ mt: loading ? 0 : 2 }}
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={20} color="inherit" /> : null
              }
            >
              {loading ? "Subiendo..." : "Subir Video"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Modal de Confirmación */}
      <Dialog
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" align="center">
            ¿Está todo correcto?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            {/* Sala */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Sala
              </Typography>
              <Typography variant="h6">
                {getSalaName()}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Hora */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Hora Comienzo Charla
              </Typography>
              <Typography variant="h6">
                {hora}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Fecha */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Fecha de la Charla
              </Typography>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="h6">
                  {formatFechaCompleta(fecha)}
                </Typography>
                {!isToday() && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "error.main",
                      fontWeight: "bold",
                    }}
                  >
                    (Fecha no actual)
                  </Typography>
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Marca */}
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Nombre de la Marca
              </Typography>
              <Typography variant="h6">
                {marca}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmModalOpen(false)}
            variant="outlined"
            fullWidth
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmedUpload}
            variant="contained"
            color="primary"
            fullWidth
          >
            Subir Video
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadForm;
